/**
 * Lightweight in-house product-analytics client (Mixpanel-style).
 *
 * Captures the user journey — page views + key actions — and ships them to the
 * user-tenant-service `/analytics/track` endpoint in small batches. Everything
 * here is fire-and-forget and defensive: analytics must NEVER throw into the app
 * or block a render, and must never surface an error toast.
 *
 * Usage:
 *   identify({ userId, email, tenantId, isAdmin })  // once, after auth
 *   trackPageView(pathname)                          // on route change
 *   track('company_refresh', { companyId })          // explicit actions
 */

import { getApiToken, SERVICE_URLS } from '../api/authApiClient';

const SESSION_KEY = 'crediarc_analytics_session';
const SESSION_TS_KEY = 'crediarc_analytics_session_ts';
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min inactivity starts a new session
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT = 10;      // flush once this many events are queued
const MAX_QUEUE = 200;    // safety cap if the network is down for a while

let identity = { userId: null, email: null, tenantId: null, isAdmin: false };
let queue = [];
let flushTimer = null;
let started = false;

const now = () => {
  // Date.now is fine in the app runtime; guarded so a hostile override can't throw.
  try { return Date.now(); } catch { return new Date().getTime(); }
};

const uuid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return 'sid-' + Math.random().toString(36).slice(2) + now().toString(36);
};

function currentSessionId() {
  try {
    const last = Number(sessionStorage.getItem(SESSION_TS_KEY)) || 0;
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid || now() - last > SESSION_IDLE_MS) {
      sid = uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now()));
    return sid;
  } catch {
    // sessionStorage unavailable (private mode edge cases) — fall back to a volatile id.
    return identity._volatileSid || (identity._volatileSid = uuid());
  }
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  // Best-effort final flush when the tab is hidden/closed (keepalive so it survives unload).
  const finalFlush = () => flush(true);
  window.addEventListener('pagehide', finalFlush);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finalFlush();
  });
}

export function identify(info = {}) {
  identity = {
    userId: info.userId || identity.userId,
    email: info.email || identity.email,
    tenantId: info.tenantId || identity.tenantId,
    isAdmin: info.isAdmin ?? identity.isAdmin,
  };
  ensureStarted();
}

export function track(event, props = {}) {
  if (typeof window === 'undefined' || !event) return;
  ensureStarted();
  try {
    queue.push({
      event,
      props: props && typeof props === 'object' ? props : {},
      path: window.location?.pathname || null,
      sessionId: currentSessionId(),
      clientTs: now(),
      ua: navigator?.userAgent ? navigator.userAgent.slice(0, 300) : null,
      ref: document?.referrer ? document.referrer.slice(0, 300) : null,
    });
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.length >= FLUSH_AT) flush();
  } catch { /* never throw from analytics */ }
}

let lastPath = null;
export function trackPageView(pathname) {
  const p = pathname || (typeof window !== 'undefined' ? window.location.pathname : null);
  if (!p || p === lastPath) return; // de-dupe repeated fires for the same path
  lastPath = p;
  track('page_view', { path: p });
}

// --- Auto-capture: record every click with a human-readable label ---
// A single capture-phase listener records controls and visible dashboard content.
// Table clicks also carry column and row context so the journey can distinguish a
// score, limit, company, header, label, card, and genuine blank-space click.
// Curated overrides: put data-track / data-track-label="Nice label" on an element
// to control its label. Use data-track-area="Nice area" on nested layout regions
// so blank-space clicks are more useful than a generic dialog/content-area event.

export function describePage(path) {
  const p = String(path || (typeof window !== 'undefined' ? window.location.pathname : ''));
  if (!p || p === '/') return 'the Dashboard';
  if (p.includes('/portfolio-risk')) return 'Portfolio Risk Intelligence';
  if (p.startsWith('/company/') && p.includes('/analysis')) return "a company's analysis";
  if (p.startsWith('/company/') && p.includes('/custom')) return "a company's custom sources";
  if (p.startsWith('/company/')) return 'a company page';
  if (p === '/company') return 'the Companies view';
  if (p.startsWith('/settings')) return 'Settings';
  if (p.startsWith('/analytics')) return 'Product Analytics';
  if (p.startsWith('/platform/distributor')) return 'a distributor page';
  if (p.startsWith('/distributor')) return 'the Distributor Dashboard';
  if (p.startsWith('/platform/tenant')) return 'a tenant page';
  if (p.startsWith('/platform')) return 'the Platform Console';
  if (p.startsWith('/reporting')) return 'BI & Reporting';
  if (p.startsWith('/api')) return 'API & Docs';
  if (p.startsWith('/billing')) return 'Billing & Plans';
  if (p.startsWith('/questions')) return 'Questions';
  if (p.startsWith('/requeststatus')) return 'Request Status';
  if (p.startsWith('/filteredSummary')) return 'a filtered summary';
  if (p.startsWith('/uploadcompanyfiles')) return 'the file upload';
  return p;
}

const CLICK_SELECTOR = [
  'button', '[role="button"]', 'a[href]', '[role="menuitem"]', 'li.MuiMenuItem-root',
  '[role="tab"]', '[role="switch"]', '[role="option"]', '[role="checkbox"]', '[role="radio"]',
  'input[type="button"]', 'input[type="submit"]', 'input[type="checkbox"]', 'input[type="radio"]',
  'input:not([type="hidden"])', 'select', 'textarea', '[contenteditable="true"]',
  'label', '.MuiSwitch-root', '.MuiIconButton-root', '[data-track]',
].join(',');

const TABLE_CELL_SELECTOR = 'th,td,[role="columnheader"],[role="rowheader"],[role="cell"],[role="gridcell"]';
const MAX_CLICK_LABEL = 90;

function cleanClickText(value, max = MAX_CLICK_LABEL) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function labelledByText(el) {
  const ids = cleanClickText(el.getAttribute('aria-labelledby'), 200).split(' ').filter(Boolean);
  if (!ids.length || typeof document === 'undefined') return '';
  return cleanClickText(ids.map((id) => document.getElementById(id)?.textContent || '').join(' '));
}

function labelForElement(el) {
  const explicit = el.getAttribute('data-track-label') || el.getAttribute('data-track');
  if (explicit && explicit !== 'true') return cleanClickText(explicit);
  let label = cleanClickText(el.getAttribute('aria-label'));
  if (!label) label = labelledByText(el);
  if (!label && el.labels?.length) label = cleanClickText(el.labels[0].textContent);
  if (!label) label = cleanClickText(el.getAttribute('placeholder'));
  if (!label) label = cleanClickText(el.getAttribute('title'));
  if (!label) {
    const txt = cleanClickText(el.textContent);
    if (txt) label = txt;
  }
  if (!label) {
    // Icon-only control: fall back to the MUI icon's testid (e.g. "MoreVertIcon" -> "More options").
    const svg = el.querySelector('svg[data-testid]');
    if (svg) label = String(svg.getAttribute('data-testid') || '').replace(/Icon$/, '');
  }
  if (!label) label = cleanClickText(el.getAttribute('name'));
  return cleanClickText(label);
}

function controlKind(el) {
  if (el.matches('[role="tab"]')) return 'tab';
  if (el.matches('[role="switch"],.MuiSwitch-root')) return 'toggle';
  if (el.matches('[role="option"]')) return 'option';
  if (el.matches('[role="checkbox"],[role="radio"],input[type="checkbox"],input[type="radio"]')) return 'toggle';
  if (el.matches('[role="menuitem"],li.MuiMenuItem-root')) return 'menu';
  if (el.matches('a[href]')) return 'link';
  if (el.matches('select')) return 'select';
  if (el.matches('input,textarea,[contenteditable="true"]')) return 'input';
  return 'button';
}

function tableCellsInRow(row) {
  return Array.from(row?.children || []).filter((el) => el.matches?.(TABLE_CELL_SELECTOR));
}

function tableContextForElement(target) {
  const cell = target.closest(TABLE_CELL_SELECTOR);
  if (!cell) return null;

  const value = cleanClickText(cell.textContent)
    || cleanClickText(cell.getAttribute('aria-label'))
    || cleanClickText(cell.getAttribute('title'));
  const isColumn = cell.matches('th,[role="columnheader"]');
  const isRowHeader = cell.matches('[role="rowheader"]');
  if (isColumn) return { label: value || 'unnamed column', kind: 'column' };

  const row = cell.closest('tr,[role="row"]');
  const table = cell.closest('table,[role="table"],[role="grid"]');
  const rowCells = tableCellsInRow(row);
  const cellIndex = rowCells.indexOf(cell);
  const ariaColumnIndex = cell.getAttribute('aria-colindex');
  const headers = Array.from(table?.querySelectorAll('th,[role="columnheader"]') || []);
  const header = ariaColumnIndex
    ? headers.find((candidate) => candidate.getAttribute('aria-colindex') === ariaColumnIndex)
    : headers.find((candidate) => tableCellsInRow(candidate.closest('tr,[role="row"]')).indexOf(candidate) === cellIndex);
  const column = cleanClickText(header?.textContent)
    || cleanClickText(header?.getAttribute('aria-label'));

  const rowHeader = row?.querySelector('[role="rowheader"]');
  const firstCell = rowCells[0];
  const rowLabelElement = rowHeader || (firstCell !== cell ? firstCell : null);
  const rowLabel = cleanClickText(rowLabelElement?.textContent)
    || cleanClickText(rowLabelElement?.getAttribute?.('aria-label'));

  return {
    label: value || column || (isRowHeader ? 'row heading' : 'empty table cell'),
    kind: isRowHeader ? 'row' : 'cell',
    ...(column ? { column } : {}),
    ...(rowLabel ? { row: rowLabel } : {}),
  };
}

function semanticContentForElement(target) {
  const table = tableContextForElement(target);
  if (table) return table;

  const image = target.closest('img,[role="img"]');
  if (image) {
    const label = cleanClickText(image.getAttribute('alt')) || cleanClickText(image.getAttribute('aria-label'));
    if (label) return { label, kind: 'image' };
  }

  const heading = target.closest('h1,h2,h3,h4,h5,h6,[role="heading"]');
  if (heading) {
    const label = cleanClickText(heading.textContent) || cleanClickText(heading.getAttribute('aria-label'));
    if (label) return { label, kind: 'heading' };
  }

  const textElement = target.closest('p,legend,caption,dt,dd,small,strong,em,.MuiChip-label');
  if (textElement) {
    const label = cleanClickText(textElement.textContent) || cleanClickText(textElement.getAttribute('aria-label'));
    if (label) return { label, kind: 'label' };
  }

  const section = target.closest('section,article,[role="region"],[data-track-section]');
  if (section) {
    const label = cleanClickText(section.getAttribute('data-track-section'))
      || cleanClickText(section.getAttribute('aria-label'))
      || cleanClickText(section.querySelector?.('h1,h2,h3,h4,h5,h6,[role="heading"]')?.textContent);
    if (label) return { label, kind: 'section' };
  }
  return null;
}

function pageAreaForElement(el) {
  const trackedArea = el.closest('[data-track-area]');
  const trackedAreaLabel = cleanClickText(trackedArea?.getAttribute('data-track-area'));
  if (trackedAreaLabel) return trackedAreaLabel;

  const region = el.closest('nav,aside,header,footer,main,[role="dialog"],[role="navigation"],[role="main"]');
  if (!region) return 'page area';
  if (region.matches('nav,[role="navigation"]')) return 'navigation area';
  if (region.matches('aside')) return 'sidebar area';
  if (region.matches('header')) return 'header area';
  if (region.matches('footer')) return 'footer area';
  if (region.matches('[role="dialog"]')) {
    const dialogName = cleanClickText(region.getAttribute('data-track-dialog'))
      || cleanClickText(region.getAttribute('aria-label'))
      || labelledByText(region)
      || cleanClickText(region.querySelector?.('.MuiDialogTitle-root,[role="heading"],h1,h2,h3')?.textContent)
      || 'unnamed';
    const subregion = el.closest('.MuiDialogTitle-root,.MuiDialogContent-root,.MuiDialogActions-root');
    const part = subregion?.matches('.MuiDialogTitle-root') ? 'header'
      : subregion?.matches('.MuiDialogContent-root') ? 'content'
      : subregion?.matches('.MuiDialogActions-root') ? 'actions'
      : 'body';
    return `${dialogName} dialog ${part}`;
  }
  return 'content area';
}

function dialogContextForElement(target) {
  const dialog = target.closest('[role="dialog"]');
  if (!dialog) return null;
  const dialogName = cleanClickText(dialog.getAttribute('data-track-dialog'))
    || cleanClickText(dialog.getAttribute('aria-label'))
    || labelledByText(dialog)
    || cleanClickText(dialog.querySelector?.('.MuiDialogTitle-root,[role="heading"],h1,h2,h3')?.textContent)
    || 'unnamed';
  const subregion = target.closest('.MuiDialogTitle-root,.MuiDialogContent-root,.MuiDialogActions-root');
  const dialogPart = subregion?.matches('.MuiDialogTitle-root') ? 'header'
    : subregion?.matches('.MuiDialogContent-root') ? 'content'
    : subregion?.matches('.MuiDialogActions-root') ? 'actions'
    : 'body';
  const namedSection = target.closest('[data-track-area]');
  const dialogSection = cleanClickText(namedSection?.getAttribute('data-track-area'));
  return {
    dialog: dialogName,
    dialogPart,
    ...(dialogSection ? { dialogSection } : {}),
  };
}

// Exported for focused QA. Input values are never read; non-input visible content
// is included because it is the only way to distinguish dashboard labels/cells.
export function describeClickTarget(target) {
  if (!target || !target.closest) return null;
  const dialogContext = dialogContextForElement(target);
  const control = target.closest(CLICK_SELECTOR);
  if (control) {
    const label = labelForElement(control);
    const kind = controlKind(control);
    const table = tableContextForElement(target);
    return {
      label: label && label.length >= 2 ? label : `${kind} control`,
      kind,
      ...(table?.column ? { column: table.column } : {}),
      ...(table?.row ? { row: table.row } : {}),
      ...(dialogContext || {}),
    };
  }
  const semantic = semanticContentForElement(target);
  if (semantic) return { ...semantic, ...(dialogContext || {}) };
  return { label: pageAreaForElement(target), kind: 'area', ...(dialogContext || {}) };
}

export function describeClickPosition(event) {
  const x = Math.round(Number(event?.clientX));
  const y = Math.round(Number(event?.clientY));
  const viewportWidth = Math.round(Number(event?.view?.innerWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)));
  const viewportHeight = Math.round(Number(event?.view?.innerHeight || (typeof window !== 'undefined' ? window.innerHeight : 0)));
  if (!Number.isFinite(x) || !Number.isFinite(y) || viewportWidth <= 0 || viewportHeight <= 0) return null;
  return {
    clickX: x,
    clickY: y,
    viewportWidth,
    viewportHeight,
    horizontalPercent: Math.max(0, Math.min(100, Math.round((x / viewportWidth) * 100))),
    verticalPercent: Math.max(0, Math.min(100, Math.round((y / viewportHeight) * 100))),
  };
}

function onCaptureClick(e) {
  try {
    const target = e.target;
    if (!target || !target.closest) return;
    const description = describeClickTarget(target);
    if (!description) return;
    const page = describePage();
    const position = describeClickPosition(e);
    track('click', { ...description, ...(position || {}), page });
  } catch { /* analytics must never throw */ }
}

let autoCaptureStarted = false;
export function initAutoCapture() {
  if (autoCaptureStarted || typeof document === 'undefined') return;
  autoCaptureStarted = true;
  document.addEventListener('click', onCaptureClick, true); // capture phase — sees every click
}

/**
 * Send queued events. Requires an auth token (endpoint is behind checkJwt); if
 * there's no token yet we keep the queue and retry on the next tick.
 * @param {boolean} keepalive  use a keepalive fetch (for tab close / pagehide)
 */
export function flush(keepalive = false) {
  try {
    if (queue.length === 0) return;
    const token = getApiToken && getApiToken();
    if (!token) return; // not authenticated yet — retry later, keep the queue
    const base = SERVICE_URLS && SERVICE_URLS.tenant;
    if (!base) return;

    const batch = queue;
    queue = [];
    const body = JSON.stringify({ events: batch, email: identity.email || undefined });

    fetch(`${base}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
      keepalive,
    }).catch(() => {
      // On failure, requeue (bounded) so a transient blip doesn't lose the journey.
      queue = batch.concat(queue).slice(-MAX_QUEUE);
    });
  } catch { /* never throw from analytics */ }
}

export function resetAnalytics() {
  queue = [];
  identity = { userId: null, email: null, tenantId: null, isAdmin: false };
  lastPath = null;
}
